'use no memo';
/**
 * Proactive Token Refresh Hook
 *
 * Schedules token refresh 5 minutes before expiry to keep sessions alive.
 * Also handles tab visibility changes to ensure tokens are fresh when user returns.
 *
 * AIDEV-NOTE: 'use no memo' + eslint-disable - This hook intentionally omits
 * scheduleProactiveRefresh from useEffect deps to prevent infinite loops. The
 * function uses refs internally making it stable, but including it triggers
 * infinite re-runs. The React Compiler cannot optimize this pattern.
 */

import { useCallback, useEffect, useRef } from 'react';
import { getMsUntilTokenExpiry } from '@utils/auth';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { recordTokenRefreshFailure, resetTokenRefreshFailures } from '@utils/authRecovery';
import { AuthSessionState, TOKEN_REFRESH_BUFFER_MS } from './authTypes';
import { isInvalidGrantError, isNotAuthenticatedError } from './useInvalidGrantHandler';

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseProactiveTokenRefreshOptions {
  /** Current session state */
  sessionState: AuthSessionState;
  /** Function to get access token with cross-tab coordination */
  getAccessTokenWithLock: (resource?: string) => Promise<string | null>;
  /** Primary API resource for token requests */
  apiResource: string;
  /** Handler for invalid_grant errors */
  onInvalidGrant: (error: unknown) => Promise<void>;
  /**
   * Handler for circuit-breaker trips on NON-grant errors (Logto 503, network
   * outage). Optional — when omitted, transient failures are logged only and
   * the next scheduled refresh is the recovery mechanism. When provided, the
   * caller can surface a "service unavailable" UI so users see something
   * actionable instead of a silent stall.
   */
  onServiceUnavailable?: (error: unknown) => void;
  /** Session state checker for callbacks (ref-based) */
  sessionCheckers: {
    isAuthenticatedAndActive: () => boolean;
    shouldSkipProactiveRefresh: () => boolean;
  };
  /** Shared ref for storing last token (shared with cross-tab sync) */
  lastTokenRef: { current: string | null };
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Manages proactive token refresh scheduling based on JWT expiry time.
 * Refreshes tokens 5 minutes before expiry to prevent session interruptions.
 */
export function useProactiveTokenRefresh(options: UseProactiveTokenRefreshOptions): void {
  const {
    sessionState,
    getAccessTokenWithLock,
    apiResource,
    onInvalidGrant,
    onServiceUnavailable,
    sessionCheckers,
    lastTokenRef,
  } = options;

  // Timer and state refs
  const proactiveRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProactiveRefreshRunningRef = useRef<boolean>(false);

  /**
   * Schedules a proactive token refresh based on current token's expiry time.
   * Uses recursion to handle immediate refresh scenarios with a guard against infinite loops.
   */
  const scheduleProactiveRefresh = useCallback(
    async (immediateRefreshAttempts = 0) => {
      // AIDEV-NOTE: Guard against infinite recursion if tokens consistently have short lifetimes
      const MAX_IMMEDIATE_REFRESH_ATTEMPTS = 3;

      // AIDEV-NOTE: Prevent concurrent/duplicate refresh attempts that can cause infinite loops
      // Guard only applies to entry points (attempts === 0), not recursive continuations
      // Recursive calls (attempts > 0) are sequential awaited calls from the same operation,
      // so they don't need guarding - they're part of one logical refresh attempt
      if (immediateRefreshAttempts === 0) {
        if (isProactiveRefreshRunningRef.current) {
          logger.debug('Proactive refresh already running - skipping duplicate call');
          return;
        }
        isProactiveRefreshRunningRef.current = true;
      }

      // Clear any existing timer
      if (proactiveRefreshTimerRef.current) {
        clearTimeout(proactiveRefreshTimerRef.current);
        proactiveRefreshTimerRef.current = null;
      }

      // Don't schedule if not authenticated or session is blocked
      if (sessionCheckers.shouldSkipProactiveRefresh()) {
        isProactiveRefreshRunningRef.current = false;
        return;
      }

      try {
        // Get current token to check its expiry
        const token = await getAccessTokenWithLock(apiResource);
        if (!token) {
          // This runs before any session-active check, so a null token
          // here can be a legitimate transition (logout in progress,
          // session never started). Log for diagnostics but do NOT bump
          // the breaker — see sites below for the post-session-check
          // failure handling.
          logger.debug('Proactive refresh: outer token getter returned null - skipping schedule');
          isProactiveRefreshRunningRef.current = false;
          return;
        }

        // Store for visibility handler
        lastTokenRef.current = token;

        const msUntilExpiry = getMsUntilTokenExpiry(token);
        if (msUntilExpiry === null) {
          logger.warn('Could not determine token expiry - skipping proactive refresh scheduling');
          isProactiveRefreshRunningRef.current = false;
          return;
        }

        // Schedule refresh before expiry
        const timeUntilRefresh = msUntilExpiry - TOKEN_REFRESH_BUFFER_MS;

        if (timeUntilRefresh <= 0) {
          // Token expires soon - refresh now (with recursion guard)
          if (immediateRefreshAttempts >= MAX_IMMEDIATE_REFRESH_ATTEMPTS) {
            logger.warn(
              `Token refresh attempted ${MAX_IMMEDIATE_REFRESH_ATTEMPTS} times but token still near expiry - scheduling retry in 30s`,
            );
            // AIDEV-NOTE: Clear the running flag before scheduling the delayed retry
            // The next call will set it again
            isProactiveRefreshRunningRef.current = false;
            proactiveRefreshTimerRef.current = setTimeout(() => {
              proactiveRefreshTimerRef.current = null;
              void scheduleProactiveRefresh(0);
            }, 30_000);
            return;
          }

          logger.debug('Token expires soon - refreshing proactively now');
          const freshToken = await getAccessTokenWithLock(apiResource);
          if (freshToken) {
            lastTokenRef.current = freshToken;
            resetTokenRefreshFailures();
            // Schedule next refresh based on new token (increment attempt counter)
            await scheduleProactiveRefresh(immediateRefreshAttempts + 1);
          } else {
            // Outer token fetch succeeded but this immediate refresh
            // returned null. The session was active moments ago, so this
            // is an unexpected auth-state degradation. Bump the breaker
            // only (no local `isTerminal` escalation, unlike the sibling
            // throw-catch paths): the same in-memory counter is consumed
            // by the api-client response interceptor on the next 401,
            // which is the canonical place to escalate. Local escalation
            // here would risk double-firing the drawer. See issue #737
            // for the parallel interceptor-side improvement.
            const failure = recordTokenRefreshFailure();
            logger.warn(
              `Proactive refresh: immediate token getter returned null (${failure.count} consecutive)`,
            );
            isProactiveRefreshRunningRef.current = false;
          }
          return;
        }

        logger.debug(
          `Scheduling proactive token refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`,
        );

        // AIDEV-NOTE: Clear the running flag - we've successfully scheduled a future timer
        isProactiveRefreshRunningRef.current = false;

        proactiveRefreshTimerRef.current = setTimeout(() => {
          proactiveRefreshTimerRef.current = null;

          void (async () => {
            // Double-check we're still authenticated
            if (!sessionCheckers.isAuthenticatedAndActive()) {
              return;
            }

            logger.debug('Proactive token refresh triggered');
            try {
              const freshToken = await getAccessTokenWithLock(apiResource);
              if (freshToken) {
                lastTokenRef.current = freshToken;
                resetTokenRefreshFailures();
                // Schedule next refresh
                await scheduleProactiveRefresh();
              } else {
                // Post session-active check (line above): a null token is
                // an unexpected degradation, not a clean logout. Same
                // rationale as the immediate-refresh path.
                const failure = recordTokenRefreshFailure();
                logger.warn(
                  `Proactive refresh: timer token getter returned null (${failure.count} consecutive)`,
                );
              }
            } catch (error) {
              if (isInvalidGrantError(error)) {
                await onInvalidGrant(error);
              } else if (isNotAuthenticatedError(error)) {
                // SDK reports `LogtoClientError(auth.unauthorized)` when the
                // session is gone server-side (refresh token rotated away,
                // logged out elsewhere, etc). Mirror `getTokenForApi`'s
                // handling: debounce via the breaker so a one-off blip during
                // a refresh round-trip doesn't escalate, then route through
                // the grant handler — NOT `onServiceUnavailable`. Routing to
                // service-unavailable here mislabels a real session expiry as
                // a backend outage and shows the wrong drawer copy.
                const failure = recordTokenRefreshFailure();
                logger.debug(
                  `Proactive refresh: "Not authenticated" (${failure.count} consecutive):`,
                  sanitizeError(error),
                );
                if (failure.isTerminal) {
                  logger.warn(
                    'Proactive refresh tripped the circuit breaker on "Not authenticated" - escalating to handleInvalidGrant',
                  );
                  await onInvalidGrant(error);
                }
              } else {
                const failure = recordTokenRefreshFailure();
                logger.warn(
                  `Proactive refresh failed (${failure.count} consecutive):`,
                  sanitizeError(error),
                );
                if (failure.isTerminal) {
                  // Surface as 'service unavailable' rather than
                  // 'session expired'. The error is not invalid_grant, so
                  // forwarding to the grant handler would mislabel a backend
                  // outage as an auth problem.
                  logger.warn(
                    'Proactive refresh tripped the circuit breaker - showing service-unavailable UI',
                  );
                  onServiceUnavailable?.(error);
                }
              }
            }
          })();
        }, timeUntilRefresh);
      } catch (error) {
        isProactiveRefreshRunningRef.current = false;
        if (isInvalidGrantError(error)) {
          await onInvalidGrant(error);
        } else if (isNotAuthenticatedError(error)) {
          // See the timer-callback catch above for the rationale; same
          // breaker-then-grant-handler routing applies to the outer
          // scheduling path so a real session expiry surfaces the
          // session-expired drawer instead of service-unavailable.
          const failure = recordTokenRefreshFailure();
          logger.debug(
            `Proactive schedule: "Not authenticated" (${failure.count} consecutive):`,
            sanitizeError(error),
          );
          if (failure.isTerminal) {
            logger.warn(
              'Proactive schedule tripped the circuit breaker on "Not authenticated" - escalating to handleInvalidGrant',
            );
            await onInvalidGrant(error);
          }
        } else {
          const failure = recordTokenRefreshFailure();
          logger.warn(
            `Error scheduling proactive refresh (${failure.count} consecutive):`,
            sanitizeError(error),
          );
          if (failure.isTerminal) {
            logger.warn(
              'Proactive schedule tripped the circuit breaker - showing service-unavailable UI',
            );
            onServiceUnavailable?.(error);
          }
        }
      }
    },
    [
      apiResource,
      getAccessTokenWithLock,
      lastTokenRef,
      onInvalidGrant,
      onServiceUnavailable,
      sessionCheckers,
    ],
  );

  // Start proactive refresh when authenticated
  useEffect(() => {
    if (sessionState === AuthSessionState.AUTHENTICATED) {
      // AIDEV-NOTE: Reset the running flag on effect re-run to allow fresh scheduling
      isProactiveRefreshRunningRef.current = false;
      void scheduleProactiveRefresh();
    }

    return () => {
      if (proactiveRefreshTimerRef.current) {
        clearTimeout(proactiveRefreshTimerRef.current);
        proactiveRefreshTimerRef.current = null;
      }
      // AIDEV-NOTE: Also reset the running flag on cleanup
      isProactiveRefreshRunningRef.current = false;
    };
    // AIDEV-NOTE: Intentionally omit scheduleProactiveRefresh from deps to prevent infinite loop
    // The function is stable (uses refs) and we only want to trigger on sessionState changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState]);

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      void (async () => {
        if (document.visibilityState !== 'visible') {
          // Tab hidden - pause proactive refresh (timer will just not fire while hidden)
          return;
        }

        // Tab became visible - check if token needs refresh
        if (!sessionCheckers.isAuthenticatedAndActive()) {
          return;
        }

        const token = lastTokenRef.current;
        if (!token) {
          // No cached token - try to get one
          await scheduleProactiveRefresh();
          return;
        }

        const msUntilExpiry = getMsUntilTokenExpiry(token);
        if (msUntilExpiry !== null && msUntilExpiry < TOKEN_REFRESH_BUFFER_MS) {
          // Token expires soon - refresh immediately
          logger.debug('Tab visible - token expiring soon, refreshing now');
          try {
            const freshToken = await getAccessTokenWithLock(apiResource);
            if (freshToken) {
              lastTokenRef.current = freshToken;
              resetTokenRefreshFailures();
              await scheduleProactiveRefresh();
            } else {
              // Session was just confirmed active (line above), so a
              // null token here is an unexpected degradation. Mirror the
              // timer-callback handling.
              const failure = recordTokenRefreshFailure();
              logger.warn(
                `Visibility refresh: token getter returned null (${failure.count} consecutive)`,
              );
            }
          } catch (error) {
            if (isInvalidGrantError(error)) {
              await onInvalidGrant(error);
            } else if (isNotAuthenticatedError(error)) {
              // Same rationale as the timer-callback and outer-scheduler
              // catches above — visibility-driven refreshes also need to
              // route session-expired errors through the grant handler.
              const failure = recordTokenRefreshFailure();
              logger.debug(
                `Visibility refresh: "Not authenticated" (${failure.count} consecutive):`,
                sanitizeError(error),
              );
              if (failure.isTerminal) {
                logger.warn(
                  'Visibility refresh tripped the circuit breaker on "Not authenticated" - escalating to handleInvalidGrant',
                );
                await onInvalidGrant(error);
              }
            } else {
              const failure = recordTokenRefreshFailure();
              logger.warn(
                `Visibility refresh failed (${failure.count} consecutive):`,
                sanitizeError(error),
              );
              if (failure.isTerminal) {
                logger.warn(
                  'Visibility refresh tripped the circuit breaker - showing service-unavailable UI',
                );
                onServiceUnavailable?.(error);
              }
            }
          }
        } else {
          // Token still valid - reschedule refresh in case timer drifted
          await scheduleProactiveRefresh();
        }
      })();
    };

    // AIDEV-NOTE: document.visibilitychange works in Capacitor WebViews (WKWebView on iOS,
    // WebView on Android). For richer app lifecycle events, @capacitor/app appStateChange
    // could be added as a future enhancement.
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    apiResource,
    getAccessTokenWithLock,
    lastTokenRef,
    onInvalidGrant,
    onServiceUnavailable,
    scheduleProactiveRefresh,
    sessionCheckers,
  ]);
}
